import { extend } from 'lodash';
/**
   * @ngdoc service
   * @name adjust.service:Modal
   * @description
   * Modal service used by {@link adjust.directive:modal modal} directive
   */
function Modal($q, $rootScope, $document, $compile, Helper) {
  'ngInject';
  /**
   * @ngdoc property
   * @name _instance
   * @propertyOf adjust.service:Modal
   *
   * @description
   * Modal instance
   *
   * @type {Object}
   * @private
   */
  var _instance;

  /**
   * @ngdoc property
   * @name _scope
   * @propertyOf adjust.service:Modal
   *
   * @description
   * Modal scope
   *
   * @type {Object}
   * @private
   */
  var _scope;

  /**
   * @ngdoc property
   * @name _element
   * @propertyOf adjust.service:Modal
   *
   * @description
   * Modal dom element
   *
   * @type {Object}
   * @private
   */
  var _element;

  /**
   * @ngdoc property
   * @name _body
   * @propertyOf adjust.service:Modal
   *
   * @description
   * Modal parent element
   *
   * @type {Object}
   * @private
   */
  var _body;

  /**
   * @ngdoc property
   * @name _tid
   * @propertyOf adjust.service:Modal
   *
   * @description
   * Modal timeout id promise
   *
   * @type {Object}
   * @private
   */
  var _tid;

  /**
   * @ngdoc property
   * @name _defaultOptions
   * @propertyOf adjust.service:Modal
   *
   * @description
   * Modal default options
   *
   * @type {Object}
   * @private
   */
  var _defaultOptions = {
    title: gettext('label.confirmation'),
    size: 'small',
    confirmBtn: gettext('action.ok'),
    cancelBtn: gettext('action.cancel'),
    parent: 'body',
    hideCancel: false,
    hideConfirm: false,
    message: gettext('message.are-you-sure')
  };

  return {
    init,
    requestPassword,
    getInstance,
    registerTid
  };

  /**
   * @ngdoc method
   * @name init
   * @methodOf adjust.service:Modal
   * @description
   * Initiate modal open, compile modal template and prepare deferred object
   *
   * @param {Object} extendedOptions modal options
   * @param {string} extendedOptions.title modal title
   * @param {string} extendedOptions.size modal size
   * @param {string} extendedOptions.confirmBtn modal confirm button text
   * @param {string} extendedOptions.cancelBtn modal cancel button text
   * @param {string} extendedOptions.parent modal parent container (query selector)
   * @param {Object} extendedOptions.scope modal directive scope
   * @param {string} extendedOptions.tpl modal template url
   * @param {string} extendedOptions.message custom message
   * @param {string} extendedOptions.transparent if backdrop is transparent
   * @param {string} extendedOptions.hideCancel if cancel button is hidden
   * @param {string} extendedOptions.hideConfirm if confirm button is hidden
   * @param {Object} extendedOptions.initialModel optional initial model value
   * @returns {Object} modal instance
   */
  function init(extendedOptions) {

    var options = {
      ..._defaultOptions,
      confirmBtn: t(_defaultOptions.confirmBtn),
      cancelBtn: t(_defaultOptions.cancelBtn),
      title: t(_defaultOptions.title),
      message: t(_defaultOptions.message),
    };

    if (!extendedOptions.tpl) {
      throw new Error('tpl option is required');
    }

    if (_instance) {
      throw new Error('There can be only one (one modal instance already initiated)');
    }

    extend(options, extendedOptions);

    _scope = (options.scope || $rootScope).$new(true);

    _scope.$on('$destroy', _handleDestroy);

    _instance = _createInstance(extendedOptions);

    var tpl = angular.element('<modal></modal>');
    var classes = ['modal-window'].concat(options.classes || []);

    if (options.transparent) {
      classes.push('transparent');
    }

    tpl.attr({
      'id': 'modal-window',
      'class': classes.join(' '),
      'transparent': options.transparent,
      'title': options.title,
      'size': options.size,
      'confirm-btn': options.confirmBtn,
      'cancel-btn': options.cancelBtn,
      'align': options.align,
      'message': options.message,
      'hide-cancel': options.hideCancel,
      'hide-confirm': options.hideConfirm,
      'is-confirm': options.classes?.includes('confirm-msg')
    });

    _element = $compile(tpl)(_scope);

    _body = $document[0].querySelector(options.parent);

    if (!_body) {
      _body = $document[0].querySelector('body');
    }

    _body.appendChild(_element[0]);

    return _instance;

  }

  /**
   * @returns {Promise}
   * @private
   */
  function requestPassword(options = {}) {
    return init({
      parent: '#sidebar-view',
      tpl: require('./modal-password.html'),
      classes: ['helper-msg', 'confirm-msg', 'confirm-icons-only', 'neutral-color'],
      title: '',
      message: '',
      initialModel: {
        password: '',
        message: t('label.enter-password'),
        confirm: (event, model) => {
          event.preventDefault();
          getInstance().close(model);
        }
      },
      ...options
    }).result;
  }

  /**
   * @ngdoc method
   * @name _createInstance
   * @methodOf adjust.service:Modal
   * @description
   * Create an instance of the modal
   *
   * @param {Object} extendedOptions modal options
   * @returns {Object} modal instance
   * @private
   */
  function _createInstance(extendedOptions) {

    var deferred = $q.defer();

    return {
      deferred: deferred,
      result: deferred.promise,
      initialModel: extendedOptions.initialModel,
      tpl: extendedOptions.tpl,
      close: function(model) {
        return _close(model);
      },
      cancel: function() {
        return _cancel();
      }
    };

  }

  /**
   * @ngdoc method
   * @name _close
   * @methodOf adjust.service:Modal
   * @description
   * Resolves the instance's promise and destroy the modal
   *
   * @param {*} model general model of the modal
   * @private
   */
  function _close(model) {

    if (!_instance) { return; }

    _instance.deferred.resolve(model);
    _destroy();

  }

  /**
   * @ngdoc method
   * @name _cancel
   * @methodOf adjust.service:Modal
   * @description
   * Rejects the instance's promise and destroy the modal
   *
   * @private
   */
  function _cancel() {

    if (!_instance) { return; }

    _instance.deferred.reject();
    _destroy();

  }

  /**
   * @ngdoc method
   * @name _destroy
   * @methodOf adjust.service:Modal
   * @description
   * Destroys modal's instance, scope, element and parent element
   *
   * @private
   */
  function _destroy() {

    _instance = null;

    _scope.$destroy();

    _scope = null;

    _body.removeChild(_element[0]);

    _element = null;

    _body = null;

    Helper.destroyTid(_tid);

  }

  /**
   * @ngdoc method
   * @name getInstance
   * @methodOf adjust.service:Modal
   * @description
   * Get the current instance of the modal
   */
  function getInstance() {

    return _instance;

  }

  /**
   * @ngdoc method
   * @name _handleDestroy
   * @methodOf adjust.service:Modal
   * @description
   * Ensure that the modal is destroyed for sure
   *
   * @private
   */
  function _handleDestroy() {

    if (!_instance) { return; }

    _cancel();

  }

  /**
   * @ngdoc method
   * @name registerTid
   * @methodOf adjust.service:Modal
   *
   * @description
   * Register timeout promise for later destroy
   *
   * @param {Object} tid timeout promise
   */
  function registerTid(tid) {

    _tid = tid;

  }
}


export { Modal };
